/**
 * OmniLeagues — Public Login + Create Account
 *
 * Full-bleed stadium hero on the left (multi-sport crowd) and a minimal
 * right-side sign-in / register card. No public-facing messaging about
 * access codes or the closed-beta program — unauthorized visitors simply
 * see the forms and cannot proceed without a valid code (which is
 * enforced server-side).
 */
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { AlertCircle, Loader2, ArrowRight, Eye, EyeOff, Lock } from 'lucide-react';

// Served from /app/frontend/public/hero/login-hero.jpg
const HERO_IMG = '/hero/login-hero.jpg';

export default function Login() {
  const { login, register, forgotPassword } = useAuth();
  const [mode, setMode] = useState('signin'); // 'signin' | 'register' | 'forgot'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPwd, setShowPwd] = useState(false);

  const [signIn, setSignIn] = useState({ email: '', password: '' });
  const [reg, setReg] = useState({
    email: '', password: '', confirm: '',
    first_name: '', last_name: '', phone: '', access_code: '',
  });
  const [forgotEmail, setForgotEmail] = useState('');

  const clearMsg = () => { setError(''); setSuccess(''); };

  const handleSignIn = async (e) => {
    e.preventDefault(); clearMsg(); setLoading(true);
    const res = await login(signIn.email, signIn.password);
    if (!res.success) setError(res.error || 'Sign in failed');
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault(); clearMsg();
    if (reg.password.length < 8) return setError('Password must be at least 8 characters');
    if (reg.password !== reg.confirm) return setError('Passwords do not match');
    const code = (reg.access_code || '').trim();
    if (!code) return setError('Access code is required');
    setLoading(true);
    const { confirm, ...body } = reg;
    const clean = { ...body, access_code: code };
    const res = await register(clean);
    if (!res.success) {
      setError(res.error || 'Could not create account');
      setLoading(false);
      return;
    }
    // AUTH_SPEC: account created but NOT signed in. Show a success
    // banner, pre-fill the email on the sign-in form, and switch modes.
    setSuccess(`Account created for ${reg.email}. Please sign in with your new credentials.`);
    setSignIn({ email: reg.email, password: '' });
    setReg({ email: '', password: '', confirm: '', first_name: '', last_name: '', phone: '', access_code: '' });
    setMode('signin');
    setLoading(false);
  };

  const handleForgot = async (e) => {
    e.preventDefault(); clearMsg(); setLoading(true);
    const res = await forgotPassword(forgotEmail);
    if (res.success) setSuccess('If that email exists, a reset link has been sent.');
    else setError(res.error || 'Could not send reset link');
    setLoading(false);
  };

  const inputCls =
    'bg-transparent border-0 border-b border-slate-800 rounded-none px-0 h-12 text-slate-100 ' +
    'placeholder:text-slate-600 focus-visible:ring-0 focus-visible:border-amber-500 text-base';
  const labelCls = 'text-[11px] tracking-[0.18em] text-slate-500 uppercase';

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 text-slate-100" style={{ backgroundColor: '#0B0B0B' }} data-testid="public-login-page">
      {/* LEFT — full-bleed hero */}
      <aside className="relative hidden lg:block overflow-hidden">
        <img
          src={HERO_IMG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
          data-testid="login-hero"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-black/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 z-10">
          <div className="text-3xl font-semibold tracking-tight drop-shadow-lg">OmniLeagues</div>
          <p className="mt-2 text-sm text-slate-100/90 max-w-md leading-relaxed drop-shadow">
            One platform for every sport, season, schedule, roster, and registration.
          </p>
        </div>
      </aside>

      {/* RIGHT — sign-in panel */}
      <main className="flex items-center justify-center px-6 py-10 lg:px-20 relative" style={{ backgroundColor: '#0B0B0B' }}>
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-6">
            <div className="text-2xl font-semibold">OmniLeagues</div>
          </div>

          {mode === 'signin' && (
            <form onSubmit={handleSignIn} className="space-y-5" data-testid="login-form">
              <h1 className="text-3xl tracking-[0.1em] font-bold">SIGN IN</h1>
              <div className="space-y-4">
                <div>
                  <label className={labelCls}>Email</label>
                  <Input type="email" required value={signIn.email} onChange={e => setSignIn(p => ({ ...p, email: e.target.value }))}
                    placeholder="you@company.com" className={inputCls} data-testid="login-email" autoComplete="email" />
                </div>
                <div>
                  <label className={labelCls}>Password</label>
                  <div className="relative">
                    <Input type={showPwd ? 'text' : 'password'} required value={signIn.password}
                      onChange={e => setSignIn(p => ({ ...p, password: e.target.value }))}
                      placeholder="Your password" className={`${inputCls} pr-10`} data-testid="login-password" autoComplete="current-password" />
                    <button type="button" onClick={() => setShowPwd(v => !v)}
                      className="absolute right-0 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                      {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </div>

              {error && <AuthAlert type="error" msg={error} />}
              {success && <AuthAlert type="success" msg={success} />}

              <Button type="submit" disabled={loading}
                className="w-full h-12 bg-[#1e3a8a] hover:bg-[#1e40af] text-white tracking-[0.18em] rounded-sm text-sm"
                data-testid="login-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>SIGN IN <ArrowRight className="h-4 w-4 ml-2" /></>}
              </Button>

              {/* Forgot password — right-aligned immediately under the
                  Sign In button, matching the reference layout. */}
              <div className="flex justify-end">
                <button type="button" onClick={() => { setMode('forgot'); clearMsg(); }}
                  className="text-sm text-slate-300 hover:text-white" data-testid="forgot-link">
                  Forgot password?
                </button>
              </div>

              <a href="/signin-phone" className="flex items-center justify-center gap-2 w-full h-10 rounded-md border border-slate-600 text-sm text-white hover:border-amber-400" data-testid="signin-phone-link">
                No password? Sign in with your phone or email
              </a>

              <div className="text-center text-sm text-slate-400 pt-2">
                Don't have an account?{' '}
                <button type="button" onClick={() => { setMode('register'); clearMsg(); }}
                  className="text-white font-semibold hover:text-amber-400" data-testid="switch-to-register">
                  Create account
                </button>
              </div>
            </form>
          )}

          {mode === 'register' && (
            <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
              <h1 className="text-3xl tracking-[0.1em] font-bold">CREATE ACCOUNT</h1>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>First name</label>
                  <Input required value={reg.first_name} onChange={e => setReg(p => ({ ...p, first_name: e.target.value }))} className={inputCls} data-testid="reg-first-name" />
                </div>
                <div>
                  <label className={labelCls}>Last name</label>
                  <Input required value={reg.last_name} onChange={e => setReg(p => ({ ...p, last_name: e.target.value }))} className={inputCls} data-testid="reg-last-name" />
                </div>
              </div>
              <div>
                <label className={labelCls}>Email</label>
                <Input type="email" required value={reg.email} onChange={e => setReg(p => ({ ...p, email: e.target.value }))} className={inputCls} data-testid="reg-email" />
              </div>
              <div>
                <label className={labelCls}>Phone</label>
                <Input type="tel" value={reg.phone} onChange={e => setReg(p => ({ ...p, phone: e.target.value }))} className={inputCls} data-testid="reg-phone" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Password</label>
                  <Input type="password" minLength={8} required value={reg.password} onChange={e => setReg(p => ({ ...p, password: e.target.value }))} className={inputCls} data-testid="reg-password" />
                </div>
                <div>
                  <label className={labelCls}>Confirm</label>
                  <Input type="password" minLength={8} required value={reg.confirm} onChange={e => setReg(p => ({ ...p, confirm: e.target.value }))} className={inputCls} data-testid="reg-confirm" />
                </div>
              </div>
              <div>
                <label className={`${labelCls} flex items-center gap-1`}>
                  <Lock className="h-3 w-3" /> Access code
                </label>
                <Input required value={reg.access_code} onChange={e => setReg(p => ({ ...p, access_code: e.target.value.toUpperCase() }))}
                  className={inputCls} autoComplete="off" data-testid="reg-access-code" />
              </div>

              {error && <AuthAlert type="error" msg={error} />}
              {success && <AuthAlert type="success" msg={success} />}

              <Button type="submit" disabled={loading}
                className="w-full h-12 bg-[#1e3a8a] hover:bg-[#1e40af] text-white tracking-[0.18em] rounded-sm text-sm"
                data-testid="register-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>CREATE ACCOUNT <ArrowRight className="h-4 w-4 ml-2" /></>}
              </Button>

              <div className="text-center text-sm text-slate-400">
                Have an account?{' '}
                <button type="button" onClick={() => { setMode('signin'); clearMsg(); }}
                  className="text-white font-semibold hover:text-amber-400" data-testid="switch-to-signin">
                  Sign in
                </button>
              </div>
            </form>
          )}

          {mode === 'forgot' && (
            <form onSubmit={handleForgot} className="space-y-6" data-testid="forgot-form">
              <h1 className="text-3xl tracking-[0.1em] font-bold">RESET PASSWORD</h1>
              <p className="text-sm text-slate-400">Enter your email and we'll send you a reset link.</p>
              <div>
                <label className={labelCls}>Email</label>
                <Input type="email" required value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} className={inputCls} data-testid="forgot-email" />
              </div>
              {error && <AuthAlert type="error" msg={error} />}
              {success && <AuthAlert type="success" msg={success} />}
              <div className="flex gap-3">
                <Button type="button" variant="ghost" onClick={() => { setMode('signin'); clearMsg(); }} className="flex-1 text-slate-400 hover:text-slate-200 border border-slate-800 h-12 rounded-sm">
                  Back
                </Button>
                <Button type="submit" disabled={loading} className="flex-1 h-12 bg-[#1e3a8a] hover:bg-[#1e40af] text-white tracking-[0.18em] rounded-sm text-sm" data-testid="forgot-submit">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'SEND LINK'}
                </Button>
              </div>
            </form>
          )}
        </div>

        {/* Trademark / copyright — auto-updates each calendar year. */}
        <div className="absolute bottom-4 left-0 right-0 text-center text-[11px] text-slate-600 tracking-wide" data-testid="login-copyright">
          © {new Date().getFullYear()} OmniLeagues<sup className="text-[8px]">TM</sup> · Semprion. All rights reserved.
          <span className="mx-1">·</span>
          <a href="/privacy" className="hover:text-slate-400 underline" data-testid="login-privacy-link">Privacy Policy</a>
        </div>
      </main>
    </div>
  );
}

function AuthAlert({ type, msg }) {
  const palette = type === 'success'
    ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
    : 'bg-red-500/10 border-red-500/30 text-red-300';
  return (
    <div className={`flex items-start gap-2 text-sm border rounded-sm px-3 py-2 ${palette}`} data-testid={`alert-${type}`}>
      <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
      <span>{msg}</span>
    </div>
  );
}
